import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { type Dispatch, type UnknownAction } from "redux";
import { Warehouse } from "lucide-react";

import {
  latestMobileVersion,
  latestMobileVersionReset,
  listAggregatorAdministrators,
  listAggregatorAdministratorsReset,
} from "../../actions";
import { PageBreadcrumb } from "../../components/PageBreadcrumb";
import { getCountryObjectFromCache } from "../../utils";
import { type AggregatorAdmin } from "../Aggregators/components/AdminsTable/aggregator-admins-response";
import { AdministratorsTable } from "./AdministratorsTable";

const ALL_ITEMS_PAGE_SIZE = 9999;

const NO_ADMINISTRATORS: AggregatorAdmin[] = [];

interface RootState {
  listAggregatorAdministrators: {
    data: { results?: AggregatorAdmin[] };
    isFetching: boolean;
  };
  latestMobileVersion: {
    data: { version?: string };
  };
}

export const AggregatorAdministrators = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<Dispatch<UnknownAction>>();
  const countryName = getCountryObjectFromCache()?.name ?? "";

  const administrators = useSelector(
    (state: RootState) => state.listAggregatorAdministrators.data.results ?? NO_ADMINISTRATORS
  );
  const isFetching = useSelector(
    (state: RootState) => state.listAggregatorAdministrators.isFetching
  );
  const latestAppVersion = useSelector(
    (state: RootState) => state.latestMobileVersion.data.version?.trim() ?? ""
  );

  useEffect(() => {
    // `active: true` excludes archived administrators: the list endpoint serves them
    // by default so the aggregator detail page can offer to re-enable them.
    dispatch(
      listAggregatorAdministrators({
        page_size: ALL_ITEMS_PAGE_SIZE,
        active: true,
      }) as unknown as UnknownAction
    );
    dispatch(latestMobileVersion() as unknown as UnknownAction);
    return () => {
      dispatch(listAggregatorAdministratorsReset() as unknown as UnknownAction);
      dispatch(latestMobileVersionReset() as unknown as UnknownAction);
    };
  }, [dispatch]);

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-4">
        <PageBreadcrumb
          items={[
            { label: t("header.overview"), href: "/wfpManager" },
            { label: t("home.manager.overview.administratorsModal.breadcrumb") },
          ]}
        />
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-x-2">
            <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 text-ivory-600 bg-ivory-100">
              <Warehouse />
            </span>
            <h1 className="font-semibold text-2xl">
              {t("home.manager.overview.administratorsModal.title", { country: countryName })}
            </h1>
          </div>
          <p className="text-muted-foreground">
            {t("home.manager.overview.administratorsModal.description", {
              version: latestAppVersion,
            })}
          </p>
        </div>
      </div>

      <AdministratorsTable
        administrators={administrators}
        isFetchingAdministrators={isFetching}
        showQuickFilters
      />
    </main>
  );
};
